import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Heart, Shield, Users, Sparkles, Clock, DollarSign } from 'lucide-react';
export function HowWeWork() {
  const principles = [{
    icon: <Heart className="h-12 w-12" data-id="element-782" />,
    title: 'Community Care Over Growth',
    description: 'We prioritize the wellbeing of our community over expansion or profit. We move at the speed of trust, not the speed of capitalism.',
    practices: ["We don't take on more students than we can support well", 'We build in rest periods between cohorts', 'We check in with our community regularly', "We're transparent about our capacity and limitations"]
  }, {
    icon: <Users className="h-12 w-12" data-id="element-783" />,
    title: 'Collaboration Over Competition',
    description: "We don't believe in the lone genius myth. All our work is collaborative, and we credit our lineage and influences.",
    practices: ['We cite our sources and name our teachers', 'We build partnerships with other organizations', 'We share resources and knowledge freely', "We celebrate other people's work in this space"]
  }, {
    icon: <Shield className="h-12 w-12" data-id="element-784" />,
    title: 'Boundaries as Care',
    description: "We are clear about what we do and don't do. Boundaries protect our energy so we can sustain this work for the long haul.",
    practices: ["We don't engage with diet talk or weight loss discourse", "We don't do free labor for organizations with budgets", "We protect our team's time and energy", "We say no to opportunities that don't align with our values"]
  }, {
    icon: <Sparkles className="h-12 w-12" data-id="element-785" />,
    title: 'Joy as Resistance',
    description: 'Liberation work includes rest, play, and pleasure. Fat people deserve joy, and we build it into everything we do.',
    practices: ['We celebrate fat joy in our content and community', 'We make space for laughter and creativity', 'We honor rest as a political act', 'We center pleasure and delight, not just struggle']
  }, {
    icon: <Clock className="h-12 w-12" data-id="element-786" />,
    title: 'Process Over Perfection',
    description: "We value learning, iteration, and growth. We don't expect ourselves or our community to be perfect—we expect accountability.",
    practices: ['We acknowledge when we mess up', 'We make space for messy unlearning', 'We revise our materials based on feedback', 'We practice harm reduction, not purity politics']
  }, {
    icon: <DollarSign className="h-12 w-12" data-id="element-787" />,
    title: 'Economic Justice',
    description: 'We believe in paying people for their labor and making our work accessible regardless of income.',
    practices: ['We offer sliding scale pricing on all courses', 'We pay our contributors and facilitators well', 'We provide full scholarships when needed', "We're transparent about our finances"]
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-788">
      <HeroSection title="How We Work" subtitle="The principles and practices that guide everything we do." tag="About ReGrowth" bgColor="lime" textColor="navy" data-id="element-789" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center" data-id="element-790">
        <p className="text-xl text-gray-700 leading-relaxed" data-id="element-791">
          These aren't aspirational values we hope to embody someday. These are{' '}
          <strong className="text-brand-lime" data-id="element-792">active practices</strong> we
          commit to daily, fail at sometimes, and hold ourselves accountable to.
        </p>
      </section>

      {/* Principles - Alternating Layout */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-793">
        <div className="space-y-20" data-id="element-794">
          {principles.map((principle, index) => <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`} data-id="element-795">
              {/* Icon & Title */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`} data-id="element-796">
                <div className="text-brand-lime mb-6" data-id="element-797">{principle.icon}</div>
                <h3 className="text-3xl font-black uppercase text-brand-black mb-4" data-id="element-798">
                  {principle.title}
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed" data-id="element-799">
                  {principle.description}
                </p>
              </div>

              {/* Practices */}
              <div className={`bg-white border-2 border-brand-black p-8 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`} data-id="element-800">
                <h4 className="text-sm font-bold uppercase text-brand-lime mb-6" data-id="element-801">
                  How We Practice This:
                </h4>
                <div className="space-y-4" data-id="element-802">
                  {principle.practices.map((practice, i) => <div key={i} className="flex items-start" data-id="element-803">
                      <span className="text-brand-lime mr-3 mt-1 flex-shrink-0" data-id="element-804">
                        ✓
                      </span>
                      <p className="text-gray-700" data-id="element-805">{practice}</p>
                    </div>)}
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Decision Making */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-806">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-807">
          <h2 className="text-3xl font-black uppercase mb-12 text-center" data-id="element-808">
            How We Make Decisions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-809">
            <div className="bg-white/10 p-8 backdrop-blur-sm border border-white/20" data-id="element-810">
              <h3 className="text-xl font-bold text-brand-lime mb-4" data-id="element-811">
                Team Decisions
              </h3>
              <p className="text-gray-200 mb-4" data-id="element-812">
                Day-to-day operational decisions are made by our core team
                through consensus when possible, with clear decision-making
                authority when needed.
              </p>
              <p className="text-sm text-gray-400" data-id="element-813">
                Examples: Course scheduling, content planning, hiring
              </p>
            </div>

            <div className="bg-white/10 p-8 backdrop-blur-sm border border-white/20" data-id="element-814">
              <h3 className="text-xl font-bold text-brand-lime mb-4" data-id="element-815">
                Community Input
              </h3>
              <p className="text-gray-200 mb-4" data-id="element-816">
                We regularly seek feedback from our community on programming,
                policies, and direction through surveys, town halls, and
                advisory groups.
              </p>
              <p className="text-sm text-gray-400" data-id="element-817">
                Examples: New course topics, accessibility features, pricing
              </p>
            </div>

            <div className="bg-white/10 p-8 backdrop-blur-sm border border-white/20" data-id="element-818">
              <h3 className="text-xl font-bold text-brand-lime mb-4" data-id="element-819">
                Accountability
              </h3>
              <p className="text-gray-200 mb-4" data-id="element-820">
                When we cause harm or make mistakes, we commit to transparent
                accountability processes that center those harmed.
              </p>
              <p className="text-sm text-gray-400" data-id="element-821">
                Examples: Public apologies, policy changes, reparations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Finances */}
      <section className="py-20 bg-white" data-id="element-822">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-823">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-824">
            Financial Transparency
          </h2>

          <div className="space-y-8" data-id="element-825">
            <div className="border-l-4 border-brand-lime pl-6" data-id="element-826">
              <h3 className="text-xl font-bold mb-3" data-id="element-827">How We're Funded</h3>
              <p className="text-gray-700 mb-4" data-id="element-828">
                ReGrowth is funded through course fees, publication
                subscriptions, and occasional grants. We do not accept funding
                from diet companies, pharmaceutical companies, or organizations
                that profit from anti-fatness.
              </p>
              <div className="bg-gray-50 p-4 text-sm" data-id="element-829">
                <p className="font-bold mb-2" data-id="element-830">2023 Revenue Breakdown:</p>
                <ul className="space-y-1 text-gray-600" data-id="element-831">
                  <li data-id="element-832">• Course fees: 65%</li>
                  <li data-id="element-833">• Publication subscriptions: 20%</li>
                  <li data-id="element-834">• Grants: 10%</li>
                  <li data-id="element-835">• Donations: 5%</li>
                </ul>
              </div>
            </div>

            <div className="border-l-4 border-brand-lime pl-6" data-id="element-836">
              <h3 className="text-xl font-bold mb-3" data-id="element-837">Where Money Goes</h3>
              <p className="text-gray-700 mb-4" data-id="element-838">
                We prioritize paying people well and keeping our programs
                accessible. Here's how we allocate our budget:
              </p>
              <div className="bg-gray-50 p-4 text-sm" data-id="element-839">
                <p className="font-bold mb-2" data-id="element-840">2023 Expense Breakdown:</p>
                <ul className="space-y-1 text-gray-600" data-id="element-841">
                  <li data-id="element-842">• Staff salaries & contractor fees: 50%</li>
                  <li data-id="element-843">• Scholarships & sliding scale subsidies: 20%</li>
                  <li data-id="element-844">• Technology & operations: 15%</li>
                  <li data-id="element-845">• Content creation & contributor payments: 10%</li>
                  <li data-id="element-846">• Community programs: 5%</li>
                </ul>
              </div>
            </div>

            <div className="border-l-4 border-brand-lime pl-6" data-id="element-847">
              <h3 className="text-xl font-bold mb-3" data-id="element-848">
                Sliding Scale Philosophy
              </h3>
              <p className="text-gray-700" data-id="element-849">
                We use sliding scale pricing on all our courses and programs.
                Those who can pay more subsidize those who can pay less. We
                never turn anyone away for lack of funds—if you need a
                scholarship, just ask.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-brand-lime/10" data-id="element-850">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-851">
          <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-852">
            Questions About How We Work?
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-853">
            We're committed to transparency. If you have questions about our
            practices, finances, or decision-making, we're happy to talk.
          </p>
          <a href="mailto:team@regrowth.com" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-brand-navy bg-brand-lime hover:bg-brand-lime/90 transition-all shadow-lg hover:-translate-y-1 uppercase tracking-wide" data-id="element-854">
            Contact Us
          </a>
        </div>
      </section>
    </div>;
}